import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { Outlet } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import Layout from './components/Layout/Layout'

// Landing Page
import Home from './pages/Home/Home'
import AgendaLabLanding from './pages/Landing/AgendaLabLanding'
import RentalTechLanding from './pages/Landing/RentalTechLanding'
import RentalBizLanding from './pages/Landing/RentalBizLanding'
import MaterialCalculatorLanding from './pages/Landing/MaterialCalculatorLanding'
import AutomationRoiLanding from './pages/Landing/AutomationRoiLanding'
import SegmentedCatalogLanding from './pages/Landing/SegmentedCatalogLanding'

// Auth Pages
import Login from './pages/Auth/Login'
import Register from './pages/Auth/Register'
import ForgotPassword from './pages/Auth/ForgotPassword'
import ResetPassword from './pages/Auth/ResetPassword'

// Dashboard - productos adquiridos
import DashboardProducts from './pages/Dashboard/Dashboard'

// RentalBiz - Property Management Module
import RentalBizDashboard from './pages/RentalBiz/Dashboard/Dashboard'
import PropertiesList from './pages/RentalBiz/Properties/PropertiesList'
import PropertyForm from './pages/RentalBiz/Properties/PropertyForm'
import PropertyDetail from './pages/RentalBiz/Properties/PropertyDetail'
import ContractsList from './pages/RentalBiz/Contracts/ContractsList'
import ContractForm from './pages/RentalBiz/Contracts/ContractForm'
import ContractDetail from './pages/RentalBiz/Contracts/ContractDetail'
import TenantsList from './pages/RentalBiz/Tenants/TenantsList'
import PaymentsList from './pages/RentalBiz/Payments/PaymentsList'
import PaymentDetailPage from './pages/RentalBiz/Payments/PaymentDetailPage'
import PaymentScheduler from './pages/RentalBiz/Payments/PaymentScheduler'
import ApprovalDashboard from './pages/RentalBiz/Payments/ApprovalDashboard'
import Extracts from './pages/RentalBiz/Extracts/Extracts'
import Settings from './pages/RentalBiz/Settings/Settings'
import AgendaLabDashboard from './pages/AgendaLab/Dashboard'
import AgendaLabServices from './pages/AgendaLab/Services'
import AgendaLabSchedule from './pages/AgendaLab/Schedule'
import AgendaLabScheduleDay from './pages/AgendaLab/ScheduleDay'
import AgendaLabClients from './pages/AgendaLab/Clients'
import AgendaLabCalendar from './pages/AgendaLab/Calendar'
import AgendaLabWorkload from './pages/AgendaLab/Workload'
import AgendaLabTelegram from './pages/AgendaLab/Telegram'
import PublicAgendaBooking from './pages/AgendaLab/PublicAgendaBooking'
import MaterialCalculatorFeatureGate from './pages/MaterialCalculator/FeatureGate'
import MCProjectListPage from './pages/MaterialCalculator/Projects'
import MCProjectCreatePage from './pages/MaterialCalculator/ProjectCreate'
import MCProjectDetailPage from './pages/MaterialCalculator/ProjectDetail'
import MCWizardStepPage from './pages/MaterialCalculator/WizardStep'
import MCReportsPage from './pages/MaterialCalculator/Reports'
import MCRulesetsPage from './pages/MaterialCalculator/Rulesets'
import MCRulesetDetailPage from './pages/MaterialCalculator/RulesetDetail'
import MCCatalogPage from './pages/MaterialCalculator/Catalog'
import MCSettingsPage from './pages/MaterialCalculator/Settings'
import AdminModule from './pages/Admin/AdminModule'
import RentalTechDashboard from './pages/RentalTech/Dashboard'
import RentalTechAvailability from './pages/RentalTech/Availability'
import RentalTechOrders from './pages/RentalTech/Orders'
import RentalTechMaintenance from './pages/RentalTech/Maintenance'
import RentalTechTelemetry from './pages/RentalTech/Telemetry'
import RentalTechBilling from './pages/RentalTech/Billing'
import RentalTechMobileOps from './pages/RentalTech/MobileOps'
import RentalTechEquipments from './pages/RentalTech/Equipments'
import RentalTechCustomers from './pages/RentalTech/Customers'
import BrainEmprendedorDashboard from './pages/BrainEmprendedor/Dashboard'
import BrainEmprendedorInvitations from './pages/BrainEmprendedor/Invitations'
import BrainEmprendedorRoom from './pages/BrainEmprendedor/Room'
import EntrepreneurCatalogProducts from './pages/EntrepreneurCatalog/Products'
import EntrepreneurCatalogProfile from './pages/EntrepreneurCatalog/Profile'
import EntrepreneurCatalogCategories from './pages/EntrepreneurCatalog/Categories'
import PublicCatalog from './pages/EntrepreneurCatalog/PublicCatalog'
import PublicProductDetail from './pages/EntrepreneurCatalog/PublicProductDetail'

// Components
import NotFound from './pages/NotFound'

// UI Components
import { ErrorBoundary } from './components/UI'

const APP_TITLE = 'AppEmprendedor'

const PRODUCT_TITLE_BY_SLUG = {
  rentalbiz: 'RentalBiz',
  appointments: 'AgendaLab',
  agendalab: 'AgendaLab',
  rentaltech: 'RentalTech LATAM',
  'material-calculator': 'Material Calculator',
  'roi-automatizacion': 'ROI Automatizacion',
  entrepreneurcatalog: 'CatalogoEmprendedor',
}

const toTitleCase = (value = '') =>
  value
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const getTitleFromPath = (pathname) => {
  if (pathname === '/') return APP_TITLE
  if (pathname === '/dashboard') return `${APP_TITLE} - Productos`

  if (pathname.startsWith('/rentalbiz') || pathname.startsWith('/inmobiliario/rentalbiz')) {
    return `${APP_TITLE} - RentalBiz`
  }

  if (pathname.startsWith('/agendalab') || pathname.startsWith('/appointments')) {
    return `${APP_TITLE} - AgendaLab`
  }
  if (pathname.startsWith('/rentaltech')) {
    return `${APP_TITLE} - RentalTech LATAM`
  }
  if (pathname.startsWith('/entrepreneurcatalog')) {
    return `${APP_TITLE} - CatalogoEmprendedor`
  }
  if (pathname.startsWith('/catalogo') || pathname.startsWith('/catalog')) {
    return `${APP_TITLE} - Catalogo`
  }
  if (pathname.includes('/soluciones/renta-maquinaria/rentaltech')) {
    return `${APP_TITLE} - RentalTech LATAM`
  }
  if (pathname.includes('/soluciones/alquiler-inmuebles/rentalbiz')) {
    return `${APP_TITLE} - RentalBiz LATAM`
  }
  if (pathname.includes('/soluciones/gestion-citas/agendalab')) {
    return `${APP_TITLE} - AgendaLab LATAM`
  }
  if (pathname.includes('/soluciones/calculadora-materiales/material-calculator')) {
    return `${APP_TITLE} - Material Calculator LATAM`
  }
  if (pathname.includes('/soluciones/automatizacion-procesos/roi-automatizacion')) {
    return `${APP_TITLE} - ROI Automatizacion`
  }

  if (pathname.startsWith('/products/')) {
    const slug = pathname.split('/')[2] || ''
    const productName = PRODUCT_TITLE_BY_SLUG[slug.toLowerCase()] || toTitleCase(slug)
    return `${APP_TITLE} - ${productName || 'Productos'}`
  }

  return APP_TITLE
}

const DocumentTitleManager = () => {
  const location = useLocation()

  useEffect(() => {
    document.title = getTitleFromPath(location.pathname)
  }, [location.pathname])

  return null
}

const HashScrollManager = () => {
  const location = useLocation()

  useEffect(() => {
    if (!location.hash) {
      return
    }

    const elementId = location.hash.replace(/^#/, '')
    if (!elementId) {
      return
    }

    let attempts = 0
    let timeoutId = null

    const scrollToHash = () => {
      const element = document.getElementById(elementId)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }

      attempts += 1
      if (attempts < 12) {
        timeoutId = window.setTimeout(scrollToHash, 120)
      }
    }

    scrollToHash()

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [location.pathname, location.hash])

  return null
}

const HomeRoute = () => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return <Home />
}

const LandingRouteResolver = () => {
  const { category, product } = useParams()
  const normalizedCategory = String(category || '').toLowerCase()
  const normalizedProduct = String(product || '').toLowerCase()

  if (normalizedCategory === 'renta-maquinaria' && normalizedProduct === 'rentaltech') {
    return <RentalTechLanding />
  }

  if (normalizedCategory === 'alquiler-inmuebles' && normalizedProduct === 'rentalbiz') {
    return <RentalBizLanding />
  }
  if (normalizedCategory === 'gestion-citas' && normalizedProduct === 'agendalab') {
    return <AgendaLabLanding />
  }
  if (normalizedCategory === 'calculadora-materiales' && normalizedProduct === 'material-calculator') {
    return <MaterialCalculatorLanding />
  }
  if (normalizedCategory === 'automatizacion-procesos' && normalizedProduct === 'roi-automatizacion') {
    return <AutomationRoiLanding />
  }

  return <NotFound />
}

// Componente para rutas protegidas
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    const currentPath = window.location.pathname + window.location.search
    const returnUrl = encodeURIComponent(currentPath)
    window.location.href = `/login?returnUrl=${returnUrl}`
    return null
  }
  
  return children
}

// Componente para redirigir si ya está autenticado
const PublicOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }
  
  return children
}

const RequireAdminRole = ({ children }) => {
  const { loading, user } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const roles = user?.Roles || user?.roles || []
  const normalizedRoles = roles.map((role) => String(role || '').trim().toLowerCase())
  const isAdmin = normalizedRoles.includes('admin') || normalizedRoles.includes('superadmin')
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

const BrainRoomRouteResolver = () => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Layout />
  }

  return <Outlet />
}

function App() {
  return (
    <>
      <DocumentTitleManager />
      <HashScrollManager />
      <Routes>
      {/* Rutas públicas */}
      <Route path="/" element={<HomeRoute />} />
      <Route path="/latam/soluciones/renta-maquinaria/rentaltech" element={<RentalTechLanding />} />
      <Route path="/latam/soluciones/alquiler-inmuebles/rentalbiz" element={<RentalBizLanding />} />
      <Route path="/latam/soluciones/gestion-citas/agendalab" element={<AgendaLabLanding />} />
      <Route path="/latam/soluciones/calculadora-materiales/material-calculator" element={<MaterialCalculatorLanding />} />
      <Route path="/latam/soluciones/automatizacion-procesos/roi-automatizacion" element={<AutomationRoiLanding />} />
      <Route path="/latam/soluciones/catalogo-emprendedor" element={<SegmentedCatalogLanding />} />
      <Route path="/latam/soluciones/catalogo-emprendedor/:segmentSlug" element={<SegmentedCatalogLanding />} />
      <Route path="/:region/soluciones/:category/:product" element={<LandingRouteResolver />} />
      
      <Route path="/login" element={
        <PublicOnlyRoute>
          <Login />
        </PublicOnlyRoute>
      } />

      <Route path="/register" element={
        <PublicOnlyRoute>
          <Register />
        </PublicOnlyRoute>
      } />

      <Route path="/forgot-password" element={
        <PublicOnlyRoute>
          <ForgotPassword />
        </PublicOnlyRoute>
      } />

      <Route path="/reset-password" element={
        <PublicOnlyRoute>
          <ResetPassword />
        </PublicOnlyRoute>
      } />
      <Route path="/agenda/:ownerUserId" element={<PublicAgendaBooking />} />
      <Route path="/catalogo/:slug" element={<PublicCatalog />} />
      <Route path="/catalogo/:slug/producto/:productId" element={<PublicProductDetail />} />
      <Route path="/catalog/:slug" element={<PublicCatalog />} />
      <Route path="/catalog/:slug/producto/:productId" element={<PublicProductDetail />} />
      
      {/* Dashboard con productos adquiridos */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardProducts />
        </ProtectedRoute>
      } />
      
      {/* RentalBiz - Property Management Module with Layout */}
      <Route path="/rentalbiz/seleccionar-rol" element={<Navigate to="/rentalbiz/dashboard" replace />} />

      <Route path="/rentalbiz" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/rentalbiz/dashboard" replace />} />
        <Route path="dashboard" element={<RentalBizDashboard />} />
        <Route path="properties" element={<PropertiesList />} />
        <Route path="properties/new" element={<PropertyForm />} />
        <Route path="properties/:id" element={<PropertyDetail />} />
        <Route path="properties/:id/edit" element={<PropertyForm />} />
        <Route path="contracts" element={<ContractsList />} />
        <Route path="contracts/new" element={<ContractForm />} />
        <Route path="contracts/:id" element={<ContractDetail />} />
        <Route path="contracts/:id/edit" element={<ContractForm />} />
        <Route path="arrendatarios" element={<TenantsList />} />
        <Route path="payments" element={<PaymentsList />} />
        <Route path="payments/:id" element={<PaymentDetailPage />} />
        <Route path="payments/scheduler" element={<PaymentScheduler />} />
        <Route path="payments/approvals" element={<ApprovalDashboard />} />
        <Route path="certificados" element={<Extracts />} />
        <Route path="configuracion" element={<Settings />} />
        <Route path="agendalab" element={<Navigate to="/agendalab" replace />} />
        <Route path="agendalab/services" element={<Navigate to="/agendalab/services" replace />} />
        <Route path="agendalab/schedule" element={<Navigate to="/agendalab/schedule" replace />} />
        <Route path="agendalab/clients" element={<Navigate to="/agendalab/clients" replace />} />
        <Route path="agendalab/calendar" element={<Navigate to="/agendalab/calendar" replace />} />
        <Route path="agendalab/workload" element={<Navigate to="/agendalab/workload" replace />} />
        <Route path="agendalab/telegram" element={<Navigate to="/agendalab/settings" replace />} />
        <Route path="agendalab/settings" element={<Navigate to="/agendalab/settings" replace />} />
      </Route>

      <Route path="/agendalab" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<AgendaLabDashboard />} />
        <Route path="dashboard" element={<Navigate to="/agendalab" replace />} />
        <Route path="services" element={<AgendaLabServices />} />
        <Route path="schedule" element={<AgendaLabSchedule />} />
        <Route path="schedule/day/:date" element={<AgendaLabScheduleDay />} />
        <Route path="clients" element={<AgendaLabClients />} />
        <Route path="calendar" element={<AgendaLabCalendar />} />
        <Route path="workload" element={<AgendaLabWorkload />} />
        <Route path="settings" element={<AgendaLabTelegram />} />
        <Route path="telegram" element={<Navigate to="/agendalab/settings" replace />} />
      </Route>


      <Route path="/material-calculator" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route element={<MaterialCalculatorFeatureGate />}>
          <Route index element={<Navigate to="/material-calculator/projects" replace />} />
          <Route path="projects" element={<MCProjectListPage />} />
          <Route path="projects/new" element={<MCProjectCreatePage />} />
          <Route path="projects/:projectId" element={<MCProjectDetailPage />} />
          <Route path="projects/:projectId/wizard" element={<Navigate to="foundation" replace />} />
          <Route path="projects/:projectId/wizard/:step" element={<MCWizardStepPage />} />
          <Route path="projects/:projectId/reports" element={<MCReportsPage />} />
          <Route path="rulesets" element={<MCRulesetsPage />} />
          <Route path="rulesets/:rulesetId" element={<MCRulesetDetailPage />} />
          <Route path="catalog" element={<MCCatalogPage />} />
          <Route path="settings" element={<MCSettingsPage />} />
        </Route>
      </Route>

      <Route path="/rentaltech" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/rentaltech/dashboard" replace />} />
        <Route path="dashboard" element={<RentalTechDashboard />} />
        <Route path="equipments" element={<RentalTechEquipments />} />
        <Route path="customers" element={<RentalTechCustomers />} />
        <Route path="availability" element={<RentalTechAvailability />} />
        <Route path="orders" element={<RentalTechOrders />} />
        <Route path="maintenance" element={<RentalTechMaintenance />} />
        <Route path="telemetry" element={<RentalTechTelemetry />} />
        <Route path="billing" element={<RentalTechBilling />} />
        <Route path="mobile-ops" element={<RentalTechMobileOps />} />
      </Route>

      <Route path="/brain-emprendedor/rooms" element={<BrainRoomRouteResolver />}>
        <Route path=":roomId" element={<BrainEmprendedorRoom />} />
      </Route>

      <Route path="/brain-emprendedor" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/brain-emprendedor/dashboard" replace />} />
        <Route path="dashboard" element={<BrainEmprendedorDashboard />} />
        <Route path="invitations" element={<BrainEmprendedorInvitations />} />
      </Route>

      <Route path="/entrepreneurcatalog" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/entrepreneurcatalog/configuration" replace />} />
        <Route path="products" element={<EntrepreneurCatalogProducts />} />
        <Route path="categories" element={<EntrepreneurCatalogCategories />} />
        <Route path="configuration" element={<EntrepreneurCatalogProfile />} />
        <Route path="profile" element={<Navigate to="/entrepreneurcatalog/configuration" replace />} />
      </Route>

      <Route path="/admin" element={
        <ProtectedRoute>
          <RequireAdminRole>
            <Layout />
          </RequireAdminRole>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/admin/licenses" replace />} />
        <Route path="licenses" element={<AdminModule />} />
        <Route path="users" element={<AdminModule />} />
        <Route path="schema" element={<AdminModule />} />
        <Route path="categories" element={<AdminModule />} />
        <Route path="products" element={<AdminModule />} />
        <Route path="landing-pages" element={<AdminModule />} />
        <Route path="landing-leads" element={<AdminModule />} />
        <Route path="landing-data" element={<AdminModule />} />
        <Route path="production-backup" element={<AdminModule />} />
      </Route>

      <Route path="/products/material-calculator" element={<Navigate to="/material-calculator/projects" replace />} />
      <Route path="/products/material-calculator/*" element={<Navigate to="/material-calculator/projects" replace />} />
      <Route path="/products/roi-automatizacion" element={<Navigate to="/latam/soluciones/automatizacion-procesos/roi-automatizacion" replace />} />
      <Route path="/products/roi-automatizacion/*" element={<Navigate to="/latam/soluciones/automatizacion-procesos/roi-automatizacion" replace />} />
      <Route path="/products/rentaltech" element={<Navigate to="/rentaltech/dashboard" replace />} />
      <Route path="/products/rentaltech/*" element={<Navigate to="/rentaltech/dashboard" replace />} />
      <Route path="/products/brain-emprendedor" element={<Navigate to="/brain-emprendedor/dashboard" replace />} />
      <Route path="/products/brain-emprendedor/*" element={<Navigate to="/brain-emprendedor/dashboard" replace />} />
      <Route path="/products/entrepreneurcatalog" element={<Navigate to="/entrepreneurcatalog/products" replace />} />
      <Route path="/products/entrepreneurcatalog/*" element={<Navigate to="/entrepreneurcatalog/products" replace />} />

      <Route path="/appointments" element={<Navigate to="/agendalab" replace />} />
      <Route path="/appointments/dashboard" element={<Navigate to="/agendalab" replace />} />
      <Route path="/appointments/services" element={<Navigate to="/agendalab/services" replace />} />
      <Route path="/appointments/schedule" element={<Navigate to="/agendalab/schedule" replace />} />
      <Route path="/appointments/clients" element={<Navigate to="/agendalab/clients" replace />} />
      <Route path="/appointments/calendar" element={<Navigate to="/agendalab/calendar" replace />} />
      <Route path="/appointments/workload" element={<Navigate to="/agendalab/workload" replace />} />
      <Route path="/appointments/telegram" element={<Navigate to="/agendalab/settings" replace />} />
      <Route path="/appointments/settings" element={<Navigate to="/agendalab/settings" replace />} />
      
      <Route path="/products/appointments" element={<Navigate to="/agendalab" replace />} />
      <Route path="/products/appointments/*" element={<Navigate to="/agendalab" replace />} />
      
      {/* Legacy routes for backwards compatibility */}
      <Route path="/inmobiliario/rentalbiz" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/rentalbiz/dashboard" replace />} />
        <Route path="dashboard" element={<RentalBizDashboard />} />
        <Route path="properties" element={<PropertiesList />} />
        <Route path="properties/new" element={<PropertyForm />} />
        <Route path="properties/:id" element={<PropertyDetail />} />
        <Route path="contracts" element={<ContractsList />} />
        <Route path="arrendatarios" element={<TenantsList />} />
        <Route path="payments" element={<PaymentsList />} />
        <Route path="certificados" element={<Extracts />} />
        <Route path="configuracion" element={<Settings />} />
      </Route>

      <Route path="/inmobiliario/rentalbiz/seleccionar-rol" element={<Navigate to="/rentalbiz/seleccionar-rol" replace />} />
      
      {/* 404 */}
      <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  )
}

export default App






